import React, { useState } from "react";
import { ChevronLeft, Filter, Search, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { groupSessionsForDisplay } from "../../hooks/sessionGrouping";

const SessionsFilters = ({
  showFilters,
  onToggleFilters,
  filters,
  onUpdateFilters,
  trainers,
  sessionTypes,
  loading,
  sessions = [],
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useTranslation();

  // Status mapping
  const sessionStatuses = [
    { value: "all", label: t("sessionsPage.filters.statuses.all") },
    { value: "scheduled", label: t("sessionsPage.filters.statuses.scheduled") },
    { value: "completed", label: t("sessionsPage.filters.statuses.completed") },
    { value: "cancelled", label: t("sessionsPage.filters.statuses.cancelled") },
    { value: "no-show", label: t("sessionsPage.filters.statuses.noShow") },
  ];

  const handleTrainerToggle = (trainerId) => {
    const currentTrainers = filters.selectedTrainers || [];
    const isSelected = currentTrainers.includes(trainerId);

    let newTrainers;
    if (isSelected) {
      newTrainers = currentTrainers.filter((id) => id !== trainerId);
    } else {
      newTrainers = [...currentTrainers, trainerId];
    }
    onUpdateFilters({ selectedTrainers: newTrainers });
  };

  const getTrainerColor = (trainerId) => {
    const colors = [
      "bg-blue-500",
      "bg-emerald-500",
      "bg-violet-500",
      "bg-amber-500",
      "bg-rose-500",
      "bg-cyan-500",
    ];
    const hash = trainerId
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const uniqueTrainers = trainers
    .filter(
      (trainer, index, self) =>
        index === self.findIndex((t) => t.id === trainer.id),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  // If hidden, show just the toggle button
  if (!showFilters) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 animate-in fade-in zoom-in duration-500">
        <button
          onClick={() => onToggleFilters(true)}
          className={`
            p-4 rounded-2xl shadow-xl transition-all duration-300 hover:scale-105 active:scale-95
            ${colors.bgTertiary} ${colors.border} border text-indigo-500 hover:text-white hover:bg-indigo-600
          `}
          title={t("sessionsPage.filters.title")}
        >
          <Filter className="w-6 h-6" />
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full overflow-hidden ${colors.bgPrimary} ${colors.border} border-r`}>
      {/* Header */}
      <div
        className={`
        p-6 border-b ${colors.border} 
        flex items-center justify-between
      `}
      >
        <h2
          className={`text-sm font-black uppercase tracking-[0.2em] ${colors.textPrimary} flex items-center`}
        >
          <Filter className="w-4 h-4 mr-3 text-indigo-500" />
          {t("sessionsPage.filters.title")}
        </h2>
        <button
          onClick={() => onToggleFilters(false)}
          className={`
            p-2 rounded-xl transition-all duration-200
            ${colors.textSecondary} ${colors.bgHover} hover:${colors.textPrimary}
          `}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8 custom-scrollbar">
        <div className="space-y-3">
          <label
            className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary}`}
          >
            {t("sessionsPage.filters.searchStudent")}
          </label>
          <div className="relative group">
            <Search
              className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 ${colors.textMuted} group-focus-within:text-indigo-500 transition-colors`}
            />
            <input
              type="text"
              placeholder={t("sessionsPage.filters.searchPlaceholder")}
              value={filters.studentSearch || ""}
              onChange={(e) =>
                onUpdateFilters({ studentSearch: e.target.value })
              }
              className={`
                w-full pl-11 pr-4 py-3 
                ${colors.bgTertiary} ${colors.border} border
                rounded-xl text-xs font-bold ${colors.inputText} 
                focus:outline-none focus:ring-1 focus:ring-indigo-500/50 focus:border-indigo-500
                transition-all duration-300 placeholder:text-zinc-600
              `}
            />
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label
              className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary}`}
            >
              {t("sessionsPage.filters.trainers")} (
              {filters.selectedTrainers?.length || 0})
            </label>
            {filters.selectedTrainers?.length > 0 && (
              <button
                onClick={() => onUpdateFilters({ selectedTrainers: [] })}
                className="text-[10px] text-indigo-400 hover:text-indigo-300 font-bold uppercase tracking-wider transition-colors"
              >
                {t("sessionsPage.filters.clear")}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
            {loading ? (
              <div className="animate-pulse space-y-2">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={`h-10 rounded-xl ${colors.bgTertiary}`}
                  />
                ))}
              </div>
            ) : (
              uniqueTrainers.map((trainer) => {
                const isSelected = filters.selectedTrainers?.includes(
                  trainer.id,
                );
                const trainerSessions = sessions.filter(
                  (s) => s.trainerId === trainer.id,
                );
                const groupedCount =
                  groupSessionsForDisplay(trainerSessions).length;

                return (
                  <button
                    key={trainer.id}
                    onClick={() => handleTrainerToggle(trainer.id)}
                    className={`
                      relative group flex items-center justify-between p-2 rounded-xl transition-all duration-300 border
                      ${
                        isSelected
                          ? "bg-indigo-600/10 border-indigo-500/50 shadow-sm"
                          : `${colors.bgHover} border-transparent hover:border-indigo-500/20`
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3 overflow-hidden">
                      <div
                        className={`
                        w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-black text-white shadow-sm
                        ${getTrainerColor(trainer.id)}
                      `}
                      >
                        {trainer.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span
                        className={`text-xs font-bold truncate ${isSelected ? "text-indigo-400" : colors.textSecondary} group-hover:${colors.textPrimary} transition-colors`}
                      >
                        {trainer.name}
                      </span>
                      {groupedCount > 0 && (
                        <span
                          className={`
                          ml-2 text-[9px] font-black px-1.5 py-0.5 rounded-md
                          ${
                            isSelected
                              ? "bg-indigo-500 text-white"
                              : `${colors.bgTertiary} ${colors.textTertiary}`
                          }
                        `}
                        >
                          {groupedCount}
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <Check className="w-3.5 h-3.5 text-indigo-500" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="space-y-3">
          <label
            className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary}`}
          >
            {t("sessionsPage.filters.status")}
          </label>
          <div className="flex flex-wrap gap-2">
            {sessionStatuses.map((status) => {
              const isActive = filters.sessionStatus === status.value;
              return (
                <button
                  key={status.value}
                  onClick={() =>
                    onUpdateFilters({ sessionStatus: status.value })
                  }
                  className={`
                    px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-300 border
                    ${
                      isActive
                        ? "bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-500/25"
                        : `${colors.bgTertiary} ${colors.border} ${colors.textSecondary} hover:${colors.textPrimary} hover:border-indigo-500/30`
                    }
                  `}
                >
                  {status.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-3">
          <label
            className={`text-[10px] font-bold uppercase tracking-widest ${colors.textTertiary}`}
          >
            {t("sessionsPage.filters.sessionType")}
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => onUpdateFilters({ sessionType: "all" })}
              className={`
                px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-300 border
                ${
                  filters.sessionType === "all"
                    ? "bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-500/25"
                    : `${colors.bgTertiary} ${colors.border} ${colors.textSecondary} hover:${colors.textPrimary} hover:border-indigo-500/30`
                }
              `}
            >
              {t("sessionsPage.filters.all")}
            </button>
            {sessionTypes.map((type) => {
              const isActive = filters.sessionType === type;
              return (
                <button
                  key={type}
                  onClick={() => onUpdateFilters({ sessionType: type })}
                  className={`
                    px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all duration-300 border
                    ${
                      isActive
                        ? "bg-indigo-600 border-indigo-500 text-white shadow-lg shadow-indigo-500/25"
                        : `${colors.bgTertiary} ${colors.border} ${colors.textSecondary} hover:${colors.textPrimary} hover:border-indigo-500/30`
                    }
                  `}
                >
                  {type}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active Filters Summary / Reset */}
      {(filters.selectedTrainers?.length > 0 ||
        filters.studentSearch ||
        filters.sessionType !== "all" ||
        filters.sessionStatus !== "all") && (
        <div
          className={`p-4 border-t ${colors.border} bg-opacity-40 backdrop-blur-sm`}
        >
          <button
            onClick={() =>
              onUpdateFilters({
                selectedTrainers: [],
                studentSearch: "",
                sessionType: "all",
                sessionStatus: "all",
              })
            }
            className="w-full py-3 rounded-xl bg-rose-500/10 hover:bg-rose-500 text-rose-500 hover:text-white border border-rose-500/20 hover:border-rose-500 transition-all duration-300 text-sm font-bold flex items-center justify-center"
          >
            <X className="w-4 h-4 mr-2" />
            {t("sessionsPage.filters.clearAll")}
          </button>
        </div>
      )}
    </div>
  );
};

export default SessionsFilters;
